import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

const CARD = 'rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)]';

/** The card a list of transaction lines sits in: the mono eyebrow + count header, then the day sections. */
export function TransactionListCard({ label, count, footer, className, children, ...aria }: Readonly<{
  /** The eyebrow ("Lançamentos"); without it the card has no header (the loading state). */
  label?: string;
  count?: ReactNode;
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
  'aria-label'?: string;
}>) {
  return (
    <section {...aria} className={cn('overflow-hidden', CARD, className)}>
      {label && (
        <div className="flex items-baseline justify-between border-b border-border px-5 py-3.5">
          {/* text-secondary, not tertiary: tertiary is 4.17:1 on the card, under AA for text this small. */}
          <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-secondary uppercase">{label}</p>
          {count != null && <p className="font-mono text-xs font-medium text-text-secondary tabular-nums">{count}</p>}
        </div>
      )}
      {children}
      {footer && <div className="border-t border-border-subtle p-3">{footer}</div>}
    </section>
  );
}

/** A list with nothing to show: an icon, what is missing and, when the filters caused it, the way out. */
export function TransactionListEmpty({ icon: Icon, title, action, className, ...aria }: Readonly<{
  icon: ComponentType<{ className?: string; weight?: 'duotone' }>;
  title: string;
  action?: ReactNode;
  className?: string;
  /** Names the empty card when it stands in for a labelled list region. */
  'aria-label'?: string;
}>) {
  return (
    <section {...aria} className={cn('p-8 text-center', CARD, className)}>
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-sunken">
        <Icon className="size-5 text-text-tertiary" weight="duotone" />
      </div>
      <h3 className="mt-3 text-base font-semibold">{title}</h3>
      {action}
    </section>
  );
}
